import { getViewingRegion } from '@/dataImplementation/viewingRegionWeb'
import { useGetStore } from '@/redux/hooks/hooks'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getApiUrlParameters } from '@vrw/data/src/api/discovery.api'
import { getRedDataApiPaths } from '@vrw/data/src/config'
import { Reward } from '@vrw/data/src/redux/rewards/types'
import { get } from '@vrw/data/src/utils/httpClient'
import { UseInfiniteRewardsProps } from './useInfiniteRewards'

const ITEMS_PER_PAGE = 12

const useInfiniteSpendRewards = (
  {
    brandName = '',
    isBrands = false,
    sort,
    categories = [],
    limit = ITEMS_PER_PAGE,
    enableVariantGrouping = true,
    ...restProps
  }: UseInfiniteRewardsProps<Reward[]>,
  deps: unknown[] = []
) => {
  const viewingRegion = getViewingRegion()
  const isAuthenticated = useGetStore('auth.isAuthenticated')

  const queryPath = `RED_API_DISCOVERY_REWARDS_SPEND${!isAuthenticated ? '_PUBLIC' : ''}` as const

  const { data: spendRewards, ...rest } = useInfiniteQuery({
    queryKey: ['spend-rewards', brandName, isAuthenticated, viewingRegion, sort, categories.join(','), ...deps],
    queryFn: async ({ pageParam = 0 }: { pageParam: number }): Promise<Reward[]> =>
      (
        await get(
          `${getRedDataApiPaths()[queryPath]}?${getApiUrlParameters({
            brandName,
            isBrands,
            limit,
            sort,
            categoriesData: categories.length > 0 ? { categories } : undefined,
            enableVariantGrouping,
            offset: pageParam * limit,
          })}`,
          isAuthenticated
        )
      ).rewards,

    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.length < limit) {
        return null
      }
      return lastPageParam + 1
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined
      }
      return firstPageParam - 1
    },
    initialPageParam: 0,
    ...restProps,
  })

  return { rewards: spendRewards, ...rest }
}

export default useInfiniteSpendRewards
